import type { DrpFormat, DrpChecklistRow, EducationChecklistRow } from "common/models/types";
import { normalizeForMatch } from "utils/drpChecklistConstants";
import {
  guidanceOf,
  toDisplayDrpRow,
  toDisplayEducationRow,
  DrpDisplayRow,
  EducationDisplayRow,
  GuidanceSourceRow,
} from "utils/checklistGuidance";

/**
 * 보고서 수정 띠 판정과 「바뀐 표 가져오기」의 표시 승계(spec 설계 결정 19·21).
 *
 * 행 키 = type과 summary를 공백 제거 후 이은 문자열. 순서·no·severity 차이는 무시한다.
 * `drpSource`만 다른 경우(원본→수정본, 내용 동일)는 drpFormat·키가 같으므로 자동으로 "같음"이다.
 * DRP 표와 교육 표는 키를 **따로** 맞춘다 — 두 표에 같은 키가 있어도 서로의 표시를 덮지 않는다(설계 결정 21 ③).
 */
export interface DrpChecklistSnapshot {
  drpFormat: DrpFormat;
  /** 저장 행(`checked` 있음)·화면 행(`guidance`만)·추출 행 모두 받는다. */
  drpItems: Array<Omit<DrpChecklistRow, "checked"> & GuidanceSourceRow>;
  educationItems: Array<Omit<EducationChecklistRow, "checked"> & GuidanceSourceRow>;
}

/** 화면 행 + "새 항목" 배지 플래그(저장하지 않음). */
export type MergedDrpRow = DrpDisplayRow;
export type MergedEducationRow = EducationDisplayRow;

export interface DrpDiffResult {
  /** 다르면 띠를 띄운다. */
  changed: boolean;
  /** 「바뀐 표 가져오기」가 쓸 행 — current 기준, 저장본에서 표시(O·X·빈 칸) 승계 + 새 행에 isNew. */
  mergedDrpItems: MergedDrpRow[];
  mergedEducationItems: MergedEducationRow[];
}

type KeyedRow = { type: string; summary: string } & GuidanceSourceRow;

const rowKey = (r: { type: string; summary: string }): string =>
  `${normalizeForMatch(r.type)}${normalizeForMatch(r.summary)}`;

function sameKeys(a: KeyedRow[], b: KeyedRow[]): boolean {
  const ka = new Set(a.map(rowKey));
  const kb = new Set(b.map(rowKey));
  if (ka.size !== kb.size) return false;
  for (const k of Array.from(ka)) if (!kb.has(k)) return false;
  return true;
}

/** 표 하나의 키 → 저장본 표시(O·X·빈 칸). */
function guidanceByKey(rows: KeyedRow[]): Map<string, ReturnType<typeof guidanceOf>> {
  const m = new Map<string, ReturnType<typeof guidanceOf>>();
  for (const r of rows) m.set(rowKey(r), guidanceOf(r));
  return m;
}

export function diffDrpChecklist(
  stored: DrpChecklistSnapshot,
  current: DrpChecklistSnapshot,
): DrpDiffResult {
  const changed =
    stored.drpFormat !== current.drpFormat ||
    !sameKeys(stored.drpItems, current.drpItems) ||
    !sameKeys(stored.educationItems, current.educationItems);

  const drpMap = guidanceByKey(stored.drpItems);
  const eduMap = guidanceByKey(stored.educationItems);

  return {
    changed,
    mergedDrpItems: current.drpItems.map((r) => {
      const k = rowKey(r);
      const had = drpMap.has(k);
      return toDisplayDrpRow(
        { no: r.no, severity: r.severity, type: r.type, summary: r.summary, guidance: had ? drpMap.get(k) : undefined },
        !had,
      );
    }),
    mergedEducationItems: current.educationItems.map((r) => {
      const k = rowKey(r);
      const had = eduMap.has(k);
      return toDisplayEducationRow(
        { no: r.no, type: r.type, summary: r.summary, guidance: had ? eduMap.get(k) : undefined },
        !had,
      );
    }),
  };
}
